import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe2, Mail, Plus, Search } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import { Alert, AlertDescription, Button, Card, CardContent, Input } from '../ui';
import TorneoCard from './TorneoCard';
import InscribirGrupoModal from './InscribirGrupoModal';
import { usePermissions } from '../../hooks/usePermissions';
import { getMisTorneosDeLaRed, getResumenDeTorneos, aplicarResumen } from '../../lib/services/participaciones';
import { getMisInvitacionesTorneo } from '../../lib/services/torneoInvitaciones';
import { fixMojibake } from '../../lib/utils';

// MYKLUBB Torneos: la vida competitiva del club fuera de casa.
//
// Es la pantalla que mas se abre del modulo, y siempre con la misma pregunta -- ¿cuando
// juego? -- asi que la tarjeta la contesta antes que nada.
//
// Las invitaciones no viven aqui, tienen su propia pantalla: son una bandeja, no un
// torneo. Pero si hay pendientes se avisa, porque una invitacion sin responder caduca
// sin que nadie se entere.

const MyklubbTorneos = () => {
  const navigate = useNavigate();
  const { canManageTorneos } = usePermissions();
  const [torneos, setTorneos] = useState([]);
  const [pendientes, setPendientes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [inscribirEn, setInscribirEn] = useState(null);

  // La lista se pinta en cuanto llega; el resumen (proximo juego, inscripciones) es una
  // segunda vuelta al servidor y rellena las tarjetas despues (el dueno, 2026-09-08).
  const cargar = useCallback(async () => {
    setLoading(true);
    const [par, inv] = await Promise.all([
      getMisTorneosDeLaRed(),
      // Un coach no resuelve invitaciones: no le avisamos de las que no puede atender.
      canManageTorneos ? getMisInvitacionesTorneo() : Promise.resolve({ data: [] }),
    ]);
    const lista = par.data || [];
    setTorneos(lista);
    setPendientes((inv.data || []).length);
    setLoading(false);
    if (lista.length === 0) return;
    const { data: res } = await getResumenDeTorneos(lista.map((x) => x.id));
    if (res) setTorneos((prev) => aplicarResumen(prev, res));
  }, [canManageTorneos]);

  useEffect(() => { cargar(); }, [cargar]);

  const term = busqueda.trim().toLowerCase();
  const lista = term
    ? torneos.filter((t) => fixMojibake(t.nombre || '').toLowerCase().includes(term)
        || fixMojibake(t.liga_nombre || t.organizadora_nombre || '').toLowerCase().includes(term))
    : torneos;

  return (
    <FeaturedLayout
      title="Mis torneos"
      subtitle="Torneos y ligas donde participa tu club."
      refreshAction={cargar}
    >
      <div className="space-y-4">
        {pendientes > 0 && (
          <Alert>
            <Mail className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>
                Tienes <strong>{pendientes}</strong>{' '}
                {pendientes === 1 ? 'invitación sin responder' : 'invitaciones sin responder'}.
              </span>
              <Button size="sm" variant="outline" onClick={() => navigate('/torneos-myklubb/invitaciones')}>
                Ver invitaciones
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {torneos.length > 3 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar torneo o liga..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="pl-10"
            />
          </div>
        )}

        {loading ? (
          <p className="py-12 text-center text-sm text-muted-foreground">Cargando...</p>
        ) : lista.length === 0 ? (
          <Card>
            <CardContent className="space-y-3 py-14 text-center">
              <Globe2 className="mx-auto h-12 w-12 text-muted-foreground opacity-30" />
              {torneos.length === 0 ? (
                <>
                  <p className="font-medium">Todavía no participas en ningún torneo</p>
                  <p className="mx-auto max-w-md text-sm text-muted-foreground">
                    Aquí aparecen los torneos y ligas que organiza otra institución y a los que
                    invitan a tu club. Cuando llegue una invitación, la verás en{' '}
                    <strong>Invitaciones</strong>.
                  </p>
                  {pendientes > 0 && (
                    <Button size="sm" onClick={() => navigate('/torneos-myklubb/invitaciones')}>
                      Ver mis invitaciones
                    </Button>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No encontramos torneos con «{busqueda}».
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            {lista.map((t) => (
              <TorneoCard
                key={t.id}
                torneo={t}
                modo="participas"
                proximo={t.proximo}
                inscripciones={t.inscripciones}
                onOpen={() => navigate(`/torneos-myklubb/${t.id}`, { state: { nombre: t.nombre } })}
                acciones={canManageTorneos ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 shrink-0 text-xs"
                    onClick={() => setInscribirEn({ id: t.id, nombre: t.nombre, esLocal: false })}
                  >
                    <Plus className="mr-1 h-3.5 w-3.5" /> Inscribir grupo
                  </Button>
                ) : null}
              />
            ))}
          </div>
        )}
      </div>

      {inscribirEn && (
        <InscribirGrupoModal
          torneo={inscribirEn}
          gruposYaInscritos={[]}
          onClose={() => setInscribirEn(null)}
          onSuccess={() => { setInscribirEn(null); cargar(); }}
        />
      )}
    </FeaturedLayout>
  );
};

export default MyklubbTorneos;
